// ============================================================================
// /my-work/history — Done & archived.
//
// Ben, 2026-08-24: "THERE MUST BE A WAY TO CLEAR things you have read … and not
// see work you have already done. DO NOT KEEP recent things. If it is done, it
// is done. I am ok with a 'done' tab, but take it out of My Work."
//
// ⛔ "Take it out of My Work" means MOVE THE RECORD OUT OF THE INBOX, not
// destroy it. Nothing is deleted; the history is auditable on this page, and
// Reopen puts a row back.
//
// Reads notification_recipients directly — the nrc_select_own policy already
// restricts to the signed-in employee's own rows, and there is a real FK to
// notifications, so the embed is legitimate (no new RPC, no new view).
// Reopen is notify_triage(..., 'reactivate'), which has existed since the
// function was written.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Check, RotateCcw, Trash2 } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  useToast,
} from '../../../shared/components';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import { fmtDate } from '../../../shared/format';
import { REASON_LABEL, restoreDerived, triage } from '../hooks/use-my-work-v2';
import { ITEM_TYPE_LABEL } from '../../routing/hooks/use-my-work-data';

// A page cap, so a five-figure history cannot hang the browser. If it is ever
// reached the page SAYS SO — silent truncation is the defect this whole run is
// about.
const HISTORY_PAGE_LIMIT = 300;

// Three terminal states, because there are two kinds of row. A NOTIFICATION is
// marked done or archived on its recipient row. A DERIVED row — an open
// requirement, an incentive to validate — has no recipient row, so dismissing it
// writes to my_work_dismissals instead. All three land on this page.
type HistoryFilter = 'all' | 'done' | 'archived' | 'dismissed_item';

const FILTER_LABEL: Record<HistoryFilter, string> = {
  all: 'All',
  done: 'Done',
  archived: 'Dismissed notices',
  dismissed_item: 'Dismissed items',
};

interface HistoryRow {
  recipient_row_id: string;
  /** 'done' | 'archived' for notifications, 'dismissed_item' for a derived row. */
  status: string;
  reason: string;
  /** Set only for derived rows, so Restore knows which RPC to call. */
  derived?: { item_type: string; item_id: string };
  done_at: string | null;
  archived_at: string | null;
  updated_at: string | null;
  title: string;
  body: string | null;
  target_url: string | null;
  entity_id: string | null;
  created_at: string | null;
}

interface EmbeddedRow {
  recipient_row_id: string;
  status: string;
  reason: string;
  done_at: string | null;
  archived_at: string | null;
  updated_at: string | null;
  notifications: {
    title: string | null;
    body: string | null;
    target_url: string | null;
    entity_id: string | null;
    created_at: string | null;
  } | null;
}

export default function MyWorkHistory() {
  const { profile } = useAuth();
  const toast = useToast();
  const myEid = profile?.employee_id ?? null;
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!myEid) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Two sources, because there are two kinds of cleared work. A
        // notification has a recipient row that notify_triage moved to
        // done/archived. A DERIVED row — an open requirement, an incentive to
        // validate — has no recipient row at all, so its dismissal is recorded in
        // my_work_dismissals. Both belong on this page; RLS scopes both to me.
        const [{ data, error: e }, { data: dis, error: e2 }] = await Promise.all([
          supabase
            .from('notification_recipients')
            .select(
              'recipient_row_id, status, reason, done_at, archived_at, updated_at, notifications(title, body, target_url, entity_id, created_at)',
            )
            .in('status', ['done', 'archived'])
            // "You follow this" updates live in News and clear themselves after
            // their News day (item #835); listing them here would bury the work
            // a person cleared under hundreds of updates.
            .not('reason', 'in', '(watching,status_change)')
            // updated_at moves on every triage (trg_notification_recipients_updated),
            // so this is "most recently cleared first" without inventing a column.
            .order('updated_at', { ascending: false })
            .limit(HISTORY_PAGE_LIMIT),
          supabase
            .from('my_work_dismissals')
            .select('item_type, item_id, title, deep_link_path, dismissed_at')
            .order('dismissed_at', { ascending: false })
            .limit(HISTORY_PAGE_LIMIT),
        ]);
        if (cancelled) return;
        if (e) throw e;
        if (e2) throw e2;
        const fromNotifications: HistoryRow[] = ((data ?? []) as unknown as EmbeddedRow[]).map((r) => ({
          recipient_row_id: r.recipient_row_id,
          status: r.status,
          reason: r.reason,
          done_at: r.done_at,
          archived_at: r.archived_at,
          updated_at: r.updated_at,
          title: r.notifications?.title ?? '(notification removed)',
          body: r.notifications?.body ?? null,
          target_url: r.notifications?.target_url ?? null,
          entity_id: r.notifications?.entity_id ?? null,
          created_at: r.notifications?.created_at ?? null,
        }));
        const fromDismissals: HistoryRow[] = (
          (dis ?? []) as {
            item_type: string;
            item_id: string;
            title: string | null;
            deep_link_path: string | null;
            dismissed_at: string;
          }[]
        ).map((d) => ({
          recipient_row_id: `${d.item_type}:${d.item_id}`,
          status: 'dismissed_item',
          reason: d.item_type,
          derived: { item_type: d.item_type, item_id: d.item_id },
          done_at: null,
          archived_at: d.dismissed_at,
          updated_at: d.dismissed_at,
          title: d.title ?? `${d.item_type} ${d.item_id}`,
          body: null,
          target_url: d.deep_link_path,
          entity_id: d.item_id,
          created_at: null,
        }));
        // Each source is capped independently, so "did we truncate" can no
        // longer be inferred from the merged length — it has to be asked of both.
        setTruncated(
          fromNotifications.length === HISTORY_PAGE_LIMIT ||
            fromDismissals.length === HISTORY_PAGE_LIMIT,
        );
        setRows(
          [...fromNotifications, ...fromDismissals].sort((a, b) =>
            (b.updated_at ?? '').localeCompare(a.updated_at ?? ''),
          ),
        );
      } catch (err) {
        if (!cancelled) setError(rpcErrorMessage(err, 'Could not load your history.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [myEid, tick]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const counts = useMemo(
    () => ({
      all: rows.length,
      done: rows.filter((r) => r.status === 'done').length,
      archived: rows.filter((r) => r.status === 'archived').length,
      dismissed_item: rows.filter((r) => r.status === 'dismissed_item').length,
    }),
    [rows],
  );

  async function reopen(row: HistoryRow) {
    setBusyId(row.recipient_row_id);
    try {
      // A derived row was never a notification, so notify_triage would find
      // nothing to reactivate. Its dismissal lives in my_work_dismissals and
      // my_work_restore is what deletes it; the row then reappears the next time
      // get_my_work() is read, because that filter is a NOT EXISTS.
      const n = row.derived
        ? await restoreDerived(row.derived.item_type, row.derived.item_id)
        : await triage([row.recipient_row_id], 'reactivate');
      // ⛔ Assert the row count. Both RPCs filter on
      // recipient_eid = get_user_employee_id(), so a mis-wired call returns a
      // LOWER count rather than failing loudly. That silence is the bug.
      if (n !== 1) {
        throw new Error(
          `Reopen affected ${n} rows, expected 1. Nothing was changed on your behalf — reload and try again.`,
        );
      }
      toast.success('Back in your inbox');
      refetch();
    } catch (e) {
      toast.error(rpcErrorMessage(e, 'Could not reopen this item.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 pb-28 sm:p-6">
      <PageHeader
        eyebrow="My Work"
        title="Done &amp; archived"
        meta={
          loading
            ? 'Loading…'
            : `${visible.length} of ${rows.length} · cleared work, kept for the record`
        }
        actions={
          <Link
            to="/my-work?tab=inbox"
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-tcs-border px-3 text-sm text-tcs-text hover:bg-tcs-surface"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to My Work
          </Link>
        }
      />

      <div role="tablist" aria-label="History filters" className="mt-4 flex gap-1 border-b border-tcs-border">
        {(['all', 'done', 'archived', 'dismissed_item'] as HistoryFilter[]).map((f) => (
          <button
            key={f}
            role="tab"
            type="button"
            aria-selected={filter === f}
            onClick={() => setFilter(f)}
            className={[
              '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
              filter === f
                ? 'border-tcs-text font-medium text-tcs-text'
                : 'border-transparent text-tcs-text-secondary hover:text-tcs-text',
            ].join(' ')}
          >
            {FILTER_LABEL[f]} ({counts[f]})
          </button>
        ))}
      </div>

      {truncated && (
        <p className="mt-3 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
          Showing the {HISTORY_PAGE_LIMIT} most recently cleared items. There are older ones this
          page is not drawing.
        </p>
      )}

      {error && (
        <div className="mt-4">
          <ErrorState title="Failed to load" body={error} onRetry={refetch} />
        </div>
      )}

      {!error && loading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-20 rounded-md" />
          <Skeleton className="h-20 rounded-md" />
          <Skeleton className="h-20 rounded-md" />
        </div>
      )}

      {!error && !loading && visible.length === 0 && (
        <div className="mt-10">
          <EmptyState
            title="Nothing here yet"
            body="Work you mark Done, and anything you dismiss — a notification or a card — moves here out of My Work. Nothing is deleted, and Reopen puts any of it back."
          />
        </div>
      )}

      {!error && !loading && visible.length > 0 && (
        <ul className="mt-6 space-y-2">
          {visible.map((r) => {
            const isDone = r.status === 'done';
            const when = isDone ? r.done_at : r.archived_at;
            // A derived row has no notification reason — its "reason" is the
            // item_type it was derived from, so it reads out of the same label
            // map the inbox uses rather than showing a raw enum.
            const kindLabel = r.derived
              ? (ITEM_TYPE_LABEL[r.derived.item_type] ?? r.derived.item_type)
              : (REASON_LABEL[r.reason] ?? r.reason);
            return (
              <li
                key={r.recipient_row_id}
                className="rounded-md border border-tcs-border bg-tcs-bg p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span
                    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      isDone
                        ? 'bg-st-ok-bg text-st-ok-fg'
                        : 'bg-tcs-surface text-tcs-text-muted'
                    }`}
                  >
                    {isDone ? <Check className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
                    {isDone ? 'Done' : 'Dismissed'}
                  </span>
                  <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-muted">
                    {kindLabel}
                  </span>
                  <span className="text-tcs-text-muted">
                    {isDone ? 'completed' : 'dismissed'} {when ? fmtDate(when) : '—'}
                    {r.created_at ? ` · raised ${fmtDate(r.created_at)}` : ''}
                  </span>
                </div>

                <div className="mt-1.5 text-sm font-medium text-tcs-text">{r.title}</div>
                {r.body && (
                  <p className="mt-0.5 line-clamp-2 text-xs text-tcs-text-secondary">{r.body}</p>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<RotateCcw className="h-3.5 w-3.5" />}
                    disabled={busyId === r.recipient_row_id}
                    onClick={() => void reopen(r)}
                  >
                    Reopen
                  </Button>
                  {r.target_url && (
                    <Link
                      to={r.target_url}
                      className="inline-flex min-h-[32px] items-center gap-1 rounded-md border border-tcs-border px-2.5 text-xs text-tcs-text hover:bg-tcs-surface"
                    >
                      Open
                      <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
